import { useState, type RefObject } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface HomeChartDataDialogProps {
  open: boolean;
  title: string;
  headers: readonly string[];
  rows: readonly (readonly string[])[];
  returnFocusRef: RefObject<HTMLElement | null>;
  onOpenChange: (open: boolean) => void;
}

export function HomeChartDataDialog({
  open,
  title,
  headers,
  rows,
  returnFocusRef,
  onOpenChange,
}: HomeChartDataDialogProps) {
  const [count, setCount] = useState(50);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className="max-h-dvh overflow-hidden p-0"
        data-slot="home-chart-data"
        onCloseAutoFocus={(event) => {
          const target = returnFocusRef.current;
          if (target?.isConnected) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        <DialogHeader title="Данные таблицей" description={title} />
        <div className="min-h-0 px-6 pb-6">
          {rows.length ? (
            <>
              <Table
                dense
                viewportProps={{ tabIndex: 0, 'aria-label': title + ' — прокрутка таблицы' }}
                viewportClassName="max-h-80 overflow-auto rounded-md border border-line focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset"
              >
                <caption className="sr-only">{title} — данные таблицей</caption>
                <TableHeader>
                  <TableRow>
                    {headers.map((header, index) => (
                      <TableHead key={index} scope="col" className={index ? 'text-right' : ''}>
                        {header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, count).map((row, index) => (
                    <TableRow key={index}>
                      {row.map((value, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          className={
                            cellIndex ? 'text-right whitespace-nowrap tabular-nums' : 'whitespace-nowrap'
                          }
                        >
                          {value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {count < rows.length && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button size="sm" variant="secondary" onClick={() => setCount((value) => value + 50)}>
                    Ещё {Math.min(50, rows.length - count)}
                  </Button>
                  <span className="text-xs text-fg-muted">
                    Показано {Math.min(count, rows.length)} из {rows.length}
                  </span>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-fg-muted">Пока нет данных</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
